import React, { useState, useEffect } from "react";
import styles from "./EligibilityFormCard.module.css";
import { fetchProgramDetails } from "@/services/PreEnrollmentService";
import useIsMobile from "@/hooks/useIsMobile";
import Loader from "../Loader/Loader";
import { trackEventPreEnrolment } from "@/services/mixpanel";

interface FormData {
  Programme_Of_Interest: string;
  email: string;
  name: string;
  phone: string;
  qualification: string;
}

interface EligibilityFormCardProps {
  formData: FormData | null;
  programsList: any;
  windowHeight: any;
  onFormSubmit: (data: {
    selectedProgram: any;
    selectedNQF: string | null;
    selectedQuestions: any;
    eligibilityFormData: {
      programCode: string;
      programName: string;
      studyModes: any;
      rmatFee: number | null;
      rplFee: number | null;
      eligibilityQuestion: { [key: string]: boolean };
      currentNqfLevel: number | null;
    };
  }) => void;
}

const EligibilityFormCard: React.FC<EligibilityFormCardProps> = ({
  onFormSubmit,
  formData,
  programsList,
  windowHeight,
}) => {
  const [selectedProgram, setSelectedProgram] = useState<any>(null);
  const [studyMode, setStudyMode] = useState<string | null>(null);
  const [selectedNQF, setSelectedNQF] = useState<string | null>(null);
  const [selectedQuestions, setSelectedQuestions] = useState<any>({});
  const [isValid, setIsValid] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [priceData, setPriceData] = useState<any>(null);

  useEffect(() => {
    if (selectedProgram) {
      localStorage.setItem(
        "userSelectedProgram",
        JSON.stringify(selectedProgram?.name)
      );
      localStorage.setItem("proCode", JSON.stringify(selectedProgram?.code));
    }
  }, [selectedProgram]);

  useEffect(() => {
    const storedProgram = localStorage.getItem("userSelectedProgram");

    if (storedProgram) {
      const selectedProgramFromStorage = JSON.parse(storedProgram);
      const defaultProgram = programsList.find(
        (course: { name: string }) => course.name === selectedProgramFromStorage
      );
      setSelectedProgram(defaultProgram);
    } else if (formData?.Programme_Of_Interest) {
      const defaultProgram = programsList.find(
        (course: { name: string }) =>
          course.name === formData.Programme_Of_Interest
      );
      setSelectedProgram(defaultProgram);
    }
  }, [programsList, formData]);

  useEffect(() => {
    if (selectedProgram) {
      localStorage.setItem(
        "userSelectedProgram",
        JSON.stringify(selectedProgram?.name)
      );
      localStorage.setItem("proCode", JSON.stringify(selectedProgram?.code));
    }
  }, [selectedProgram]);

  const selectedNQFLevel = selectedProgram?.currentNQF;

  const uniqueNQFLevels: number[] = Array.from(
    new Set<number>(
      programsList
        .filter(
          (program: { currentNQF: number }) =>
            program.currentNQF && selectedNQFLevel !== undefined
        )
        .map((program: { currentNQF: number }) => program.currentNQF)
    )
  )
    .filter(
      (level: number) =>
        selectedNQFLevel !== undefined && level < selectedNQFLevel
    )
    .sort((a, b) => b - a);

  useEffect(() => {
    if (selectedProgram && selectedProgram.code) {
      fetchProgramDetails(selectedProgram.code)
        .then((data: any) => setPriceData(data))
        .catch((error: any) => console.error("Error fetching data:", error));
    }
  }, [selectedProgram]);

  useEffect(() => {
    const savedQuestions = localStorage.getItem("selectedQuestions");
    if (savedQuestions) {
      setSelectedQuestions(JSON.parse(savedQuestions));
    }
  }, []);

  // useEffect(() => {
  //   if (selectedProgram?.nqfLevel && selectedNQF === null) {
  //     const defaultNQF = parseInt(selectedProgram?.nqfLevel) - 1;
  //     setSelectedNQF(defaultNQF);
  //   }
  // }, [selectedProgram]);

  useEffect(() => {
    setIsValid(validateForm());
  }, [selectedProgram, studyMode, selectedNQF, selectedQuestions]);

  const questions = selectedProgram?.questions || [];
  const isBothRequired = selectedProgram?.isBothQuestionsRequired;

  const values = Object.values(selectedQuestions);
  const totalQuestions = questions.length;

  let selectedQuestionsValue: boolean | null = null;

  if (isBothRequired) {
    if (selectedQuestions[0] === false) {
      selectedQuestionsValue = false;
    } else if (values.length === totalQuestions) {
      selectedQuestionsValue = values.every((val) => val === true);
    }
  } else {
    if (selectedQuestions[0] === true) {
      selectedQuestionsValue = true;
    } else if (values.length === totalQuestions) {
      selectedQuestionsValue = values.some((val) => val === true)
        ? true
        : false;
    }
  }

  const handleAnswer = (index: number, value: boolean) => {
    setSelectedQuestions((prev: any) => {
      const updatedQuestions = { ...prev, [index]: value };

      if (isBothRequired) {
        if (index === 0 && value === false) {
          setCurrentQuestionIndex(0);
        } else if (value === true) {
          setCurrentQuestionIndex(index + 1);
        }
      } else {
        if (index === 0 && value === true) {
          setCurrentQuestionIndex(0);
        } else if (value === false) {
          setCurrentQuestionIndex(index + 1);
        }
      }

      return updatedQuestions;
    });
  };

  const validateForm = () => {
    const validationErrors: any = {};
    if (!selectedProgram) validationErrors.program = "Please select a program.";
    if (selectedQuestionsValue === true && !studyMode)
      validationErrors.studyMode = "Please select a study mode.";
    if (selectedQuestionsValue === null)
      validationErrors.studyMode = "Please complete all eligibility questions.";
    if (selectedQuestionsValue === false && selectedNQF === null)
      validationErrors.nqtLevel = "Please select an NQF level.";
    if (Object.values(selectedQuestions).some((value) => value === null))
      validationErrors.questions = "Please answer all eligibility questions.";
    return Object.keys(validationErrors).length === 0;
  };

  const handleFormSubmit = () => {
    if (isValid) {
      const selectedStudyMode = priceData?.data[0]?.studyModes?.find(
        (mode: any) => mode?.studyModeCode === studyMode
      );

      onFormSubmit({
        selectedProgram,
        selectedNQF,
        selectedQuestions: selectedQuestionsValue,
        eligibilityFormData: {
          programCode: priceData?.data[0]?.programCode,
          programName: priceData?.data[0]?.programName,
          studyModes: selectedStudyMode,
          rmatFee: priceData?.data[0]?.rmatFee,
          rplFee: priceData?.data[0]?.rplFee,
          eligibilityQuestion: selectedQuestions,
          currentNqfLevel: selectedProgram.nqfLevel,
        },
      });
    } else {
      console.error("Study modes are not available.");
    }
  };
  const isMobile = useIsMobile();
  return (
    <>
      {!formData ? (
        <Loader />
      ) : (
        <div className={styles.container}>
          <div className={styles.form}>
            <div className={styles.TopTag}>
              <div className={styles.headTopTag}>
                <span>ⓘ 2 minutes to get provisional enrolment</span>
              </div>
              <h2>Let’s go {formData?.name}!</h2>
              <p>
                Achieve your dream by simply checking your eligibility for your
                desired program!
              </p>
            </div>

            <div
              className={styles.formContainer}
              style={{ height: isMobile ? "auto" : `${windowHeight - 60}px` }}
            >
              <div className={styles.row}>
                {/* Program Selection */}
                <div className={styles.firstSection}>
                  <label className={styles.label}>Programme of Interest</label>
                  <select
                    className={styles.dropdown}
                    onChange={(e) => {
                      const program = programsList.find(
                        (course: { name: string }) =>
                          course.name === e.target.value
                      );
                      if (program) {
                        setSelectedProgram(program);
                        setStudyMode(null);
                        setSelectedNQF(null);
                        setSelectedQuestions({});
                        setCurrentQuestionIndex(0);
                        trackEventPreEnrolment("program_of_interest_changed", {
                          new_program: selectedProgram.name,
                        });
                      }
                    }}
                    value={selectedProgram?.name || ""}
                  >
                    <option value="" hidden>
                      Select Programme
                    </option>
                    {programsList.map((item: any) => (
                      <option key={item.id} value={item.name}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>
                {questions &&
                  questions?.map((question: any, index: number) =>
                    index <= currentQuestionIndex ? (
                      <div key={index} className={styles.nqtQuestions}>
                        <label className={styles.label}>{question.title}</label>
                        <div className={styles.radioGroup}>
                          <label className={styles.radioLabel}>
                            <input
                              type="radio"
                              name={`question${index}`}
                              checked={selectedQuestions[index] === true}
                              onChange={() => handleAnswer(index, true)}
                            />
                            Yes
                          </label>
                          <label className={styles.radioLabel}>
                            <input
                              type="radio"
                              name={`question${index}`}
                              checked={selectedQuestions[index] === false}
                              onChange={() => handleAnswer(index, false)}
                            />
                            No
                          </label>
                        </div>
                      </div>
                    ) : null
                  )}
                {selectedQuestionsValue && (
                  <div className={styles.studyMode}>
                    {(() => {
                      const studyModes = priceData?.data[0]?.studyModes || [];
                      const allowedModes = [
                        "ONLINE",
                        "FULL-TIME",
                        "DAY",
                        "WEEKEND",
                      ];

                      // Filter only allowed study modes
                      const filteredModes = studyModes?.filter((mode: any) =>
                        allowedModes.includes(mode?.studyModeCode)
                      );

                      const selectedModes: {
                        studyModeCode: string;
                        label: string;
                      }[] = [];

                      // Prioritize ONLINE first
                      const onlineMode = filteredModes.find(
                        (mode: any) => mode?.studyModeCode === "ONLINE"
                      );
                      if (onlineMode) {
                        selectedModes.push({
                          studyModeCode: "ONLINE",
                          label: "Online",
                        });
                      }

                      // Select one contact mode if available
                      const contactModes = ["FULL-TIME", "DAY", "WEEKEND"];
                      const contactMode = contactModes
                        .map((mode) =>
                          filteredModes.find(
                            (m: any) => m?.studyModeCode === mode
                          )
                        )
                        .find((mode) => mode !== undefined); // Pick the first available mode

                      if (contactMode) {
                        selectedModes.push({
                          studyModeCode: contactMode?.studyModeCode,
                          label: "Contact",
                        });
                      }

                      if (selectedModes.length === 1) {
                        const selectedMode = selectedModes[0]?.studyModeCode;
                        if (studyMode !== selectedMode)
                          setStudyMode(selectedMode);
                        return null;
                      }

                      return (
                        <>
                          <label className={styles.label}>
                            Preferred mode of study
                          </label>
                          <div className={styles.radioGroup}>
                            {selectedModes.map((mode) => (
                              <label
                                key={mode.studyModeCode}
                                className={styles.radioLabel}
                              >
                                <input
                                  type="radio"
                                  name="studyMode"
                                  value={mode?.studyModeCode}
                                  checked={studyMode === mode?.studyModeCode}
                                  onChange={() => {
                                    setStudyMode(mode?.studyModeCode);
                                    trackEventPreEnrolment(
                                      "check_eligibility_clicked",
                                      {
                                        study_mode: mode?.label,
                                      }
                                    );
                                  }}
                                />
                                {mode.label}
                              </label>
                            ))}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}

                {selectedQuestionsValue === false &&
                  uniqueNQFLevels.length > 0 && (
                    <>
                      <div className={styles.nqfLevel}>
                        <label className={styles.label}>
                          Current NQF Level
                        </label>
                        <select
                          className={styles.dropdown}
                          value={selectedNQF || ""}
                          onChange={(e) => {
                            const nqfLevel = e.target.value;
                            setSelectedNQF(nqfLevel);
                            trackEventPreEnrolment("nqf_level_selected", {
                              nqf_level: nqfLevel,
                            });
                          }}
                        >
                          <option value="" disabled>
                            Select
                          </option>
                          {uniqueNQFLevels.map((level: any) => {
                            return (
                              <option key={String(level)} value={String(level)}>
                                {level === "4"
                                  ? `Metric or NQF ${level}`
                                  : `NQF ${level}`}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </>
                  )}
                <div>
                  {selectedQuestionsValue === false && (
                    <div className={`${styles.eligibilityMessage} `}>
                      <h3>
                        Unfortunately, you do not meet the requirements for this
                        programme.
                      </h3>
                      <p>
                        Don’t worry – we have other excellent options that may
                        better suit your qualifications and aspirations
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.buttonsContainer}>
                {selectedQuestionsValue === false ? (
                  <>
                    {uniqueNQFLevels.length > 0 && (
                      <button
                        className={`${styles.submitButton} ${
                          !isValid ? styles.disabled : ""
                        }`}
                        disabled={!isValid}
                        onClick={() => {
                          handleFormSubmit();
                          trackEventPreEnrolment(
                            "find_perfect_program_clicked",
                            {
                              program_selected: selectedProgram.name,
                            }
                          );
                        }}
                      >
                        Find Your Perfect Program
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    className={`${styles.submitButton} ${
                      !isValid ? styles.disabled : ""
                    }`}
                    disabled={!isValid}
                    onClick={() => {
                      handleFormSubmit();
                      trackEventPreEnrolment("check_eligibility_clicked", {
                        program_selected: selectedProgram.name,
                      });
                    }}
                  >
                    Check Eligibility
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EligibilityFormCard;
